import React from 'react';

/**
 * Placeholder product artwork for the FarmerHome carousels — stands in
 * when a manufacturer hasn't uploaded a real image_url yet, so the
 * cards read as products at a glance instead of a wall of grey boxes.
 *
 * Six inline bottle / bag variants, picked deterministically from
 * `variantKey` (usually the product id) so the same product always
 * renders the same artwork. Vector, so no download and no CSP
 * gymnastics; each shape is drawn from two brand-adjacent hues so the
 * strip feels like one family.
 *
 * Sized to fill its container — set the width/height on the parent.
 */
export default function MockProductImage({ variantKey, className = '' }) {
  const idx = _pickVariant(variantKey);
  const v = VARIANTS[idx];
  return (
    <svg
      viewBox="0 0 108 108"
      preserveAspectRatio="xMidYMid meet"
      className={`w-full h-full block ${className}`}
      role="img"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`bg-${idx}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={v.bg[0]} />
          <stop offset="100%" stopColor={v.bg[1]} />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="108" height="108" fill={`url(#bg-${idx})`} />
      {v.shape}
    </svg>
  );
}

// One entry per variant. Keep the list at 6 or 7 so a small carousel
// of ~5 items shows visible variety; adding more is cheap.
const VARIANTS = [
  // 0 · squat bottle · sand / gold
  {
    bg: ['#F4EAD3', '#D9CBA3'],
    shape: (
      <g>
        <rect x="44" y="16" width="20" height="10" fill="#8A6D24" rx="2" />
        <rect x="36" y="26" width="36" height="16" fill="#F2E4B4" rx="2" />
        <rect x="26" y="42" width="56" height="52" fill="#F4EAD3" rx="6" stroke="#8A6D24" strokeWidth="1.5" />
        <rect x="30" y="58" width="48" height="24" fill="#3E6A46" rx="2" />
        <text x="54" y="74" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="700" fontSize="9" fill="#FFFFFF">PRO</text>
      </g>
    ),
  },
  // 1 · tall spray · sage / forest
  {
    bg: ['#E5EED1', '#B7C98E'],
    shape: (
      <g>
        <rect x="38" y="10" width="32" height="12" fill="#295233" rx="2" />
        <rect x="34" y="22" width="40" height="72" fill="#DDE7C5" rx="4" stroke="#295233" strokeWidth="1.5" />
        <rect x="38" y="38" width="32" height="30" fill="#3E6A46" rx="2" />
        <text x="54" y="58" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="700" fontSize="10" fill="#FFFFFF">500</text>
      </g>
    ),
  },
  // 2 · pouch · amber / rust
  {
    bg: ['#F5E5D1', '#DBA97C'],
    shape: (
      <g>
        <path d="M28 22 L80 22 L84 92 L24 92 Z" fill="#EFC996" stroke="#8B4515" strokeWidth="1.5" />
        <path d="M42 18 L66 18 L64 28 L44 28 Z" fill="#8B4515" />
        <rect x="34" y="44" width="40" height="26" fill="#8B4515" rx="2" />
        <text x="54" y="61" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="700" fontSize="9" fill="#FFFFFF">NPK</text>
      </g>
    ),
  },
  // 3 · barrel · deep green / sage
  {
    bg: ['#D8E7DE', '#7EA487'],
    shape: (
      <g>
        <rect x="34" y="18" width="40" height="76" fill="#C7DDCE" rx="6" stroke="#254D33" strokeWidth="1.5" />
        <rect x="30" y="26" width="48" height="8" fill="#254D33" rx="1" />
        <rect x="30" y="78" width="48" height="8" fill="#254D33" rx="1" />
        <rect x="42" y="42" width="24" height="30" fill="#254D33" />
        <text x="54" y="62" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="700" fontSize="11" fill="#FFFFFF">×</text>
      </g>
    ),
  },
  // 4 · round bottle · plum / lavender
  {
    bg: ['#EBE0F1', '#A88CC4'],
    shape: (
      <g>
        <rect x="44" y="12" width="20" height="10" fill="#5C3F86" rx="2" />
        <rect x="36" y="22" width="36" height="14" fill="#D8CAE7" rx="2" />
        <path d="M28 38 Q28 34 32 34 L76 34 Q80 34 80 38 L80 90 Q80 96 74 96 L34 96 Q28 96 28 90 Z" fill="#B29CCB" stroke="#5C3F86" strokeWidth="1.5" />
        <rect x="34" y="58" width="40" height="22" fill="#5C3F86" rx="2" />
        <text x="54" y="73" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="700" fontSize="9" fill="#FFFFFF">BIO</text>
      </g>
    ),
  },
  // 5 · sachet · terracotta / peach
  {
    bg: ['#F3D6C2', '#C8825B'],
    shape: (
      <g>
        <path d="M26 26 L82 26 L86 96 L22 96 Z" fill="#E8B48F" stroke="#8A3F1A" strokeWidth="1.5" />
        <rect x="36" y="14" width="36" height="14" fill="#8A3F1A" rx="1" />
        <rect x="34" y="50" width="40" height="24" fill="#5C2916" rx="2" />
        <text x="54" y="66" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="700" fontSize="9" fill="#FFFFFF">GRO+</text>
      </g>
    ),
  },
];

// Deterministic non-negative hash → variant index. Falls back to a
// pseudo-random pick if variantKey is empty (still stable for the
// lifetime of the app instance thanks to the module-level counter).
let _fallbackCounter = 0;
function _pickVariant(key) {
  const s = (key ?? '').toString();
  if (!s) {
    return (_fallbackCounter++) % VARIANTS.length;
  }
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % VARIANTS.length;
}
